import { useState } from 'react';
import {
  MessageSquare,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Quote,
  ArrowRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import useTranslation from '@/hooks/useTranslation';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import { getCategoryColor } from '@/utils/categoriesColorList';
import type { Category } from '@/types';

/**
 * Initial visible slice for the Categories panel. Kept compact so the
 * sidebar stays scannable for the common case (3-6 categories), but the
 * `+N more` button below the list expands to the full set. This matters
 * particularly for scale questions (1-10 rating) where every value is a
 * category — hiding the bottom of the distribution misrepresents the data.
 */
const COLLAPSED_CATEGORY_LIMIT = 8;

interface QuestionSidebarProps {
  questionId: string;
  responseCount?: number;
  categories?: Category[];
  patternCount?: number;
  divergentCount?: number;
  quoteCount?: number;
}

function StatRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs text-neutral-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-neutral-900">{value}</span>
    </div>
  );
}

function QuestionSidebar({
  questionId,
  responseCount,
  categories,
  patternCount,
  divergentCount,
  quoteCount,
}: QuestionSidebarProps) {
  const { lang } = useTranslation();
  // `open` is used for the "View all responses" CTA (no category
  // arg — preserves any active filter, just opens the drawer).
  // `toggleCategory` is used by the per-category buttons below so
  // clicking a category in the sidebar adds it to the inline
  // filter rather than wiping it and force-opening the drawer —
  // matches the chart-bar behaviour from TELV1-292.
  // `selectedCategories` drives the visual selected-state on the
  // category buttons so the sidebar list, the chart bars and the
  // inline chip row all visibly agree about what's filtered.
  const open = useResponsesPanelStore((s) => s.open);
  const toggleCategory = useResponsesPanelStore((s) => s.toggleCategory);
  const selectedCategories = useResponsesPanelStore((s) => s.selectedCategories);
  const selectedLabelSet = new Set(selectedCategories.map((c) => c.label));
  const [isExpanded, setIsExpanded] = useState(false);

  const maxCount = categories?.reduce((max, cat) => Math.max(max, cat.count ?? 0), 0) || 1;
  const totalCategories = categories?.length ?? 0;
  const hasOverflow = totalCategories > COLLAPSED_CATEGORY_LIMIT;
  const visibleCategories =
    hasOverflow && !isExpanded ? categories!.slice(0, COLLAPSED_CATEGORY_LIMIT) : categories ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Response stats */}
      {responseCount !== undefined && responseCount > 0 && (
        <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-neutral-400" />
            <h3 className="text-sm font-semibold text-neutral-900">
              {lang.get('msg.questionStats')}
            </h3>
          </div>
          <div className="flex flex-col gap-2">
            <StatRow
              icon={<MessageSquare className="h-3.5 w-3.5 text-primary" />}
              label={lang.get('msg.responses')}
              value={responseCount}
            />
            {patternCount !== undefined && patternCount > 0 && (
              <StatRow
                icon={<TrendingUp className="h-3.5 w-3.5 text-primary" />}
                label={lang.get('msg.patternsFound')}
                value={patternCount}
              />
            )}
            {divergentCount !== undefined && divergentCount > 0 && (
              <StatRow
                icon={<AlertTriangle className="h-3.5 w-3.5 text-chat-primary" />}
                label={lang.get('msg.divergentViews')}
                value={divergentCount}
              />
            )}
            {quoteCount !== undefined && quoteCount > 0 && (
              <StatRow
                icon={<Quote className="h-3.5 w-3.5 text-chat-primary" />}
                label={lang.get('msg.voiceQuotes')}
                value={quoteCount}
              />
            )}
          </div>
        </Card>
      )}

      {/* Compact category distribution */}
      {categories && categories.length > 0 && (
        <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-neutral-900">{lang.get('msg.categories')}</h3>
            <span className="text-xs text-neutral-400">{categories.length}</span>
          </div>
          <div className="flex flex-col gap-2">
            {visibleCategories.map((cat, i) => {
              const isSelected = selectedLabelSet.has(cat.label);
              return (
                <button
                  key={cat.label}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggleCategory(questionId, { label: cat.label, value: cat.label })}
                  className={`group flex items-center gap-2 text-left rounded px-1 -mx-1 py-0.5 transition-colors ${
                    isSelected ? 'bg-primary/5' : 'hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span
                        className={`text-xs truncate transition-colors ${
                          isSelected
                            ? 'text-primary font-medium'
                            : 'text-neutral-600 group-hover:text-neutral-900'
                        }`}
                      >
                        {cat.label}
                      </span>
                      <span className="text-xs text-neutral-400 shrink-0 ml-2">
                        {cat.count ?? 0}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all group-hover:opacity-80"
                        style={{
                          width: `${((cat.count ?? 0) / maxCount) * 100}%`,
                          backgroundColor: getCategoryColor(i),
                        }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
            {hasOverflow && (
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                aria-expanded={isExpanded}
                className="self-start text-xs text-neutral-500 hover:text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded px-1 -mx-1 py-0.5"
              >
                {isExpanded
                  ? lang.get('msg.showLess')
                  : `+${totalCategories - COLLAPSED_CATEGORY_LIMIT} ${lang.get('msg.nMore')}`}
              </button>
            )}
          </div>
        </Card>
      )}

      {/* View all responses — accent card. The primary path now lives
          as an inline `ResponsesZone` in the main column (first viewport),
          but many researchers will still want a fast "full filter view"
          shortcut from the sidebar. Styled as an accent card rather than
          a plain outline button so it reads as a purposeful action, not
          generic chrome. */}
      {responseCount !== undefined && responseCount > 0 && (
        <button
          type="button"
          onClick={() => open(questionId)}
          className="group flex items-center justify-between w-full text-left rounded-xl px-4 py-3 border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/5/40 hover:from-primary/10/80 hover:to-primary/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-white border border-primary/20 shadow-sm">
              <MessageSquare className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-primary">
                {lang.get('msg.viewAllResponses')}
              </span>
              <span className="text-[11px] text-primary/70">
                {responseCount}{' '}
                {responseCount === 1
                  ? lang.get('msg.response') || 'response'
                  : lang.get('msg.responses')}
              </span>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
    </div>
  );
}

export default QuestionSidebar;
